import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Wand2, Mic, FileText, Megaphone, RefreshCw, Image as ImageIcon, Copy, Music, X, Save, Play, Check, Upload } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DURATIONS = [15, 30, 60];
const inputCls = "mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]";
const selectCls = "mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]";

const empty = { business_name: "", website_url: "", max_pages: 2, extra_urls: "", notes: "", description: "", cta: "", tone: "Warm & inspirational", duration: 30, plays: 0, link: "", voice: "auto", music_bed: true, bed_style: "pad", bed_volume: 0.13, bed_start: 0, instructions: "" };

export default function AiAdGenerator({ open, onClose, onCreated, initialDraft, onInitialConsumed }) {
  const [form, setForm] = useState(empty);
  const [voices, setVoices] = useState([]);
  const [tones, setTones] = useState([]);
  const [bedStyles, setBedStyles] = useState([]);
  const [script, setScript] = useState("");
  const [sources, setSources] = useState([]);
  const [variants, setVariants] = useState([]);
  const [audio, setAudio] = useState(null);
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [bedAudio, setBedAudio] = useState(null);
  const [library, setLibrary] = useState([]);
  const [saveToLib, setSaveToLib] = useState(false);
  const bedRef = useRef(null);
  const loadLibrary = () => api.get("/ai-ads/music-library").then((r) => setLibrary(r.data)).catch(() => {});
  const deleteLibItem = async (id, e) => {
    e?.stopPropagation();
    try { await api.delete(`/ai-ads/music-library/${id}`); loadLibrary(); toast.success("Removed from library"); }
    catch { toast.error("Could not remove track"); }
  };
  const uploadBed = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("bed");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("save_to_library", saveToLib ? "true" : "false");
      fd.append("name", f.name);
      const r = await api.post("/ai-ads/bed-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBedAudio({ path: r.data.path, filename: r.data.filename });
      set("music_bed", true);
      if (r.data.saved) { loadLibrary(); toast.success("Uploaded & saved to your House Music Library"); }
      else toast.success("Custom music uploaded — it'll play under the voiceover");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not upload music"); }
    finally { setBusy(null); }
  };
  const token = localStorage.getItem("token");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const draftIdRef = useRef(null);
  const autoTimer = useRef(null);
  const previewAudio = useRef(null);
  const hydrating = useRef(false);
  const flyerRef = useRef(null);
  const logoRef = useRef(null);

  const uploadFlyer = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setBusy("flyer");
    try {
      const fd = new FormData();
      list.slice(0, 5).forEach((f) => fd.append("files", f));
      const r = await api.post("/ai-ads/flyer", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const txt = (r.data.text || "").trim();
      setForm((prev) => ({ ...prev, notes: prev.notes ? `${prev.notes}\n\n${txt}` : txt }));
      toast.success(`Read ${r.data.count} flyer${r.data.count !== 1 ? "s" : ""} — details added to notes`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read that flyer"); }
    finally { setBusy(null); }
  };

  const loadDrafts = () => api.get("/ai-ads/drafts").then((r) => setDrafts(r.data)).catch(() => {});

  useEffect(() => {
    if (!open) return;
    hydrating.current = true;
    setForm(empty); setScript(""); setSources([]); setVariants([]); setAudio(null); setLogo(null); setBusy(null); setBedAudio(null);
    setDraftId(null); draftIdRef.current = null; setSavedAt(null);
    api.get("/ai-ads/options").then((r) => { setVoices(r.data.voices); setTones(r.data.tones); setBedStyles(r.data.bed_styles || []); }).catch(() => {});
    loadLibrary();
    loadDrafts();
    if (initialDraft) { loadDraft(initialDraft); onInitialConsumed?.(); }
    const t = setTimeout(() => { hydrating.current = false; }, 300);
    return () => { clearTimeout(t); if (previewAudio.current) previewAudio.current.pause(); };
  }, [open]);

  const setDraft = (id) => { setDraftId(id); draftIdRef.current = id; };

  const playPreview = () => {
    if (previewAudio.current) previewAudio.current.pause();
    const url = `${API}/ai-ads/bed-preview?style=${form.bed_style}&volume=${form.bed_volume}&auth=${token}`;
    const a = new Audio(url);
    previewAudio.current = a;
    setPreviewing(true);
    a.onended = () => setPreviewing(false);
    a.onerror = () => { setPreviewing(false); toast.error("Could not play preview"); };
    a.play().catch(() => setPreviewing(false));
  };

  const loadDraft = (d) => {
    setForm({ business_name: d.business_name || "", website_url: d.website_url || "", max_pages: d.max_pages ?? 2, extra_urls: d.extra_urls || "", notes: d.notes || "", description: d.description || "", cta: d.cta || "", tone: d.tone || "Warm & inspirational", duration: d.duration || 30, plays: d.plays || 0, link: d.link || "", voice: d.voice || "auto", music_bed: d.music_bed ?? true, bed_style: d.bed_style || "pad", bed_volume: d.bed_volume ?? 0.13, bed_start: d.bed_start ?? 0, instructions: "" });
    setScript(d.script || ""); setVariants([]);
    setAudio(d.audio_path ? { path: d.audio_path, url: `/ai-ads/media?path=${d.audio_path}` } : null);
    setLogo(d.logo_path ? { path: d.logo_path, url: `/ai-ads/media?path=${d.logo_path}` } : null);
    setDraft(d.id);
    hydrating.current = true;
    setTimeout(() => { hydrating.current = false; }, 300);
    toast.success("Draft loaded");
  };

  const persistDraft = async (silent) => {
    const r = await api.post("/ai-ads/drafts", { id: draftIdRef.current || undefined, ...form, script, audio_path: audio?.path || "", logo_path: logo?.path || "" });
    setDraft(r.data.id);
    setSavedAt(Date.now());
    loadDrafts();
    return r.data;
  };

  // Auto-save (debounced) whenever there's something worth keeping.
  useEffect(() => {
    if (!open || hydrating.current) return;
    if (!form.business_name.trim()) return;
    clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => { persistDraft(true).catch(() => {}); }, 1600);
    return () => clearTimeout(autoTimer.current);
  }, [form, script, audio, logo, open]);

  const saveDraft = async () => {
    if (!form.business_name.trim()) { toast.error("Add a business name before saving"); return; }
    setBusy("draft");
    try {
      await persistDraft(false);
      toast.success("Draft saved — resume it anytime");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save draft"); }
    finally { setBusy(null); }
  };

  const deleteDraft = async (id, e) => {
    e?.stopPropagation();
    try { await api.delete(`/ai-ads/drafts/${id}`); if (draftId === id) setDraft(null); loadDrafts(); toast.success("Draft deleted"); }
    catch { toast.error("Could not delete draft"); }
  };

  const genScript = async (variations = 1) => {
    if (!form.business_name.trim()) { toast.error("Enter a business name first"); return; }
    setBusy(variations > 1 ? "variations" : "script");
    try {
      const r = await api.post("/ai-ads/script", { business_name: form.business_name, website_url: form.website_url, max_pages: Number(form.max_pages), extra_urls: form.extra_urls, notes: form.notes, description: form.description, cta: form.cta, tone: form.tone, duration: Number(form.duration), variations });
      if (variations > 1 && r.data.variations) {
        setVariants(r.data.variations);
        toast.success(`${r.data.variations.length} versions ready — pick your favorite`);
      } else {
        setScript(r.data.script); setVariants([]);
        toast.success("Script generated — edit it if you like");
      }
      setSources(r.data.sources || []);
      if (r.data.website_provided && !r.data.website_read) {
        toast.info("Couldn't read that website, so I wrote from the details you provided.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate script"); }
    finally { setBusy(null); }
  };

  const chooseVariant = (v) => { setScript(v); setVariants([]); toast.success("Version selected — edit it if you like"); };

  const genVoiceover = async () => {
    if (!script.trim()) { toast.error("Generate or write a script first"); return; }
    setBusy("voice");
    try {
      const r = await api.post("/ai-ads/voiceover", { script, voice: form.voice, tone: form.tone, music_bed: form.music_bed, bed_style: form.bed_style, bed_volume: form.bed_volume, bed_start: form.bed_start, bed_audio_path: bedAudio?.path || "", instructions: form.instructions });
      setAudio(r.data);
      toast.success(form.voice === "auto" ? `Voiceover ready · AI chose ${r.data.voice_label}` : "Voiceover ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate voiceover"); }
    finally { setBusy(null); }
  };

  const genLogo = async () => {
    if (!form.business_name.trim()) { toast.error("Enter a business name first"); return; }
    setBusy("logo");
    try {
      const r = await api.post("/ai-ads/logo", { business_name: form.business_name, description: form.description });
      setLogo(r.data);
      toast.success("Logo generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate logo"); }
    finally { setBusy(null); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("logo-upload");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/ai-ads/logo-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(r.data);
      toast.success("Logo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not upload logo"); }
    finally { setBusy(null); }
  };

  const create = async () => {
    if (!form.business_name.trim()) { toast.error("Business name required"); return; }
    if (!audio) { toast.error("Generate the voiceover before creating the ad"); return; }
    setBusy("create");
    try {
      await api.post("/ai-ads/create", {
        business_name: form.business_name, duration: Number(form.duration), plays: Number(form.plays) || 0,
        link_url: form.link, script, audio_path: audio.path, logo_path: logo?.path || "", draft_id: draftId || "",
      });
      toast.success("AI ad created — it's now under review");
      onCreated?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create ad"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#141C42] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-ad-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E6B34A]" /> AI Ad Generator
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 -mt-1">
          <p className="text-sm text-zinc-400">Describe the sponsor and let AI write a script, record a voiceover, and design a logo.</p>
          {savedAt && <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-emerald-400" data-testid="ai-autosave"><Check className="w-3.5 h-3.5" /> Draft auto-saved</span>}
        </div>

        {drafts.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-[#0C1338] p-3" data-testid="ai-drafts">
            <p className="overline text-zinc-400 mb-2">Resume a draft</p>
            <div className="flex flex-wrap gap-2">
              {drafts.map((d) => (
                <div key={d.id} onClick={() => loadDraft(d)} data-testid={`ai-draft-${d.id}`}
                  className={`group flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border text-sm cursor-pointer transition-colors ${draftId === d.id ? "border-[#E6B34A] bg-[#E6B34A]/10 text-[#E6B34A]" : "border-white/10 bg-white/5 text-zinc-300 hover:border-[#E6B34A]/60"}`}>
                  <span className="max-w-[160px] truncate font-semibold">{d.business_name || "Untitled"}</span>
                  <button onClick={(e) => deleteDraft(d.id, e)} data-testid={`ai-draft-del-${d.id}`} className="p-0.5 rounded-full text-zinc-500 hover:text-[#FF3B30]"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className="overline text-zinc-400">Business / sponsor name</label>
              <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Grace Family Diner" data-testid="ai-name" className={inputCls} /></div>
            <div className="sm:col-span-2"><label className="overline text-zinc-400">Website URL (optional — AI reads it to write the script)</label>
              <Input value={form.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="https://gracefamilydiner.com" data-testid="ai-website" className={inputCls} />
              {form.website_url.trim() && (
                <div className="mt-1.5 flex items-center gap-2 text-xs text-zinc-400" data-testid="ai-scan-pages-wrap">
                  <span>Scan</span>
                  <select value={form.max_pages} onChange={(e) => set("max_pages", Number(e.target.value))} data-testid="ai-scan-pages" className="rounded-md bg-[#1E2A5A] border border-white/10 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                    <option value={0}>homepage only</option>
                    <option value={2}>homepage + 2 pages</option>
                    <option value={3}>homepage + 3 pages</option>
                    <option value={4}>homepage + 4 pages</option>
                  </select>
                  <span>for deeper detail</span>
                </div>
              )}
            </div>
            <div className="sm:col-span-2"><label className="overline text-zinc-400">Specific pages to read (optional)</label>
              <Textarea value={form.extra_urls} onChange={(e) => set("extra_urls", e.target.value)} placeholder="Paste promo/landing page URLs, one per line" data-testid="ai-extra-urls" className={`${inputCls} min-h-[52px] font-mono text-xs`} />
              <p className="text-[11px] text-zinc-500 mt-1">The AI will read these exact pages too (up to 4).</p>
            </div>
            <div className="sm:col-span-2"><label className="overline text-zinc-400">Paste copy / notes (optional — great when there's no website)</label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Paste a brochure blurb, flyer text, or any notes about the sponsor…" data-testid="ai-notes" className={`${inputCls} min-h-[70px]`} />
              <input ref={flyerRef} type="file" multiple accept=".pdf,image/png,image/jpeg,image/webp" onChange={uploadFlyer} className="hidden" data-testid="ai-flyer-input" />
              <button type="button" onClick={() => flyerRef.current?.click()} disabled={busy === "flyer"} data-testid="ai-flyer-upload" className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-semibold transition-colors disabled:opacity-60">
                {busy === "flyer" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload flyers (PDF/image, up to 5)
              </button>
            </div>
            <div className="sm:col-span-2"><label className="overline text-zinc-400">What they offer</label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="A family-owned Southern comfort food restaurant open after Sunday service." data-testid="ai-desc" className={`${inputCls} min-h-[70px]`} /></div>
            <div className="sm:col-span-2"><label className="overline text-zinc-400">Call to action</label>
              <Input value={form.cta} onChange={(e) => set("cta", e.target.value)} placeholder="Come taste the blessing this Sunday" data-testid="ai-cta" className={inputCls} /></div>
            <div><label className="overline text-zinc-400">Tone</label>
              <select value={form.tone} onChange={(e) => set("tone", e.target.value)} data-testid="ai-tone" className={selectCls}>
                {(tones.length ? tones : [form.tone]).map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className="overline text-zinc-400">Length</label>
              <select value={form.duration} onChange={(e) => set("duration", e.target.value)} data-testid="ai-duration" className={selectCls}>
                {DURATIONS.map((d) => <option key={d} value={d}>{d} seconds</option>)}
              </select></div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => genScript(1)} disabled={busy === "script" || busy === "variations"} data-testid="ai-gen-script"
              className="flex-1 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {busy === "script" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {script ? "Regenerate script" : "Generate script"}
            </button>
            <button onClick={() => genScript(3)} disabled={busy === "script" || busy === "variations"} data-testid="ai-gen-variations"
              className="shrink-0 px-4 py-2.5 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {busy === "variations" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
              3 versions
            </button>
          </div>

          {variants.length > 0 && (
            <div className="space-y-2" data-testid="ai-variants">
              <p className="overline text-zinc-400">Pick a version</p>
              {variants.map((v, i) => (
                <button key={i} onClick={() => chooseVariant(v)} data-testid={`ai-variant-${i}`}
                  className="w-full text-left rounded-xl border border-white/10 bg-[#0C1338] p-3 hover:border-[#E6B34A] transition-colors">
                  <span className="overline text-[#E6B34A]">Version {i + 1} · {v.trim().split(/\s+/).filter(Boolean).length} words</span>
                  <p className="text-sm text-zinc-300 mt-1 leading-relaxed line-clamp-4">{v}</p>
                </button>
              ))}
            </div>
          )}

          {script && (
            <div className="rounded-xl border border-white/10 bg-[#0C1338] p-3" data-testid="ai-script-block">
              <label className="overline text-zinc-400 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Ad script (editable)</label>
              <Textarea value={script} onChange={(e) => setScript(e.target.value)} data-testid="ai-script" className={`${inputCls} min-h-[110px] leading-relaxed`} />
              <p className="text-[11px] text-zinc-500 mt-1">{script.trim().split(/\s+/).filter(Boolean).length} words</p>
              {sources.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="ai-sources">
                  <span className="text-[11px] text-zinc-500">Read from:</span>
                  {sources.map((u, i) => {
                    let label = u;
                    try { const p = new URL(u); label = p.pathname === "/" || !p.pathname ? p.hostname.replace(/^www\./, "") : p.pathname; } catch { /* keep raw */ }
                    return <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`ai-source-${i}`} title={u} className="text-[11px] px-1.5 py-0.5 rounded bg-white/5 text-[#E6B34A] hover:bg-white/10 truncate max-w-[180px]">{label}</a>;
                  })}
                </div>
              )}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-[#0C1338] p-3">
              <label className="overline text-zinc-400 flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Voiceover</label>
              <select value={form.voice} onChange={(e) => set("voice", e.target.value)} data-testid="ai-voice" className={selectCls}>
                <option value="auto">Auto — let AI pick</option>
                {(voices.length ? voices : [{ id: "warm_female", label: "Warm female" }]).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
              <label className="flex items-center gap-2 mt-2 text-sm text-zinc-300 cursor-pointer" data-testid="ai-music-toggle">
                <input type="checkbox" checked={form.music_bed} onChange={(e) => set("music_bed", e.target.checked)} className="accent-[#E6B34A] w-4 h-4" data-testid="ai-music-checkbox" />
                <Music className="w-3.5 h-3.5 text-[#E6B34A]" /> Add soft worship music bed
              </label>
              {form.music_bed && (
                <div className="mt-2 space-y-2 rounded-lg bg-white/5 p-2.5" data-testid="ai-bed-controls">
                  {!bedAudio && (
                    <div>
                      <label className="overline text-zinc-400">Bed style</label>
                      <div className="flex gap-2 items-center">
                        <select value={form.bed_style} onChange={(e) => set("bed_style", e.target.value)} data-testid="ai-bed-style" className={`${selectCls} flex-1`}>
                          {(bedStyles.length ? bedStyles : [{ id: "pad", label: "Ambient pad" }]).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                        <button type="button" onClick={playPreview} data-testid="ai-bed-preview"
                          className="mt-1.5 shrink-0 h-9 px-3 rounded-md bg-[#E6B34A]/15 text-[#E6B34A] hover:bg-[#E6B34A]/25 transition-colors flex items-center gap-1.5 text-sm font-semibold">
                          {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Preview
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    <input ref={bedRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac" onChange={uploadBed} className="hidden" data-testid="ai-bed-input" />
                    {bedAudio ? (
                      <div className="flex items-center gap-2 rounded-md bg-emerald-400/10 border border-emerald-400/30 px-2.5 py-1.5" data-testid="ai-bed-custom">
                        <Music className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span className="text-xs text-emerald-300 truncate flex-1">{bedAudio.filename}</span>
                        <button type="button" onClick={() => setBedAudio(null)} data-testid="ai-bed-remove" className="text-emerald-300 hover:text-white shrink-0"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <>
                        <label className="flex items-center gap-1.5 mb-1.5 text-[11px] text-zinc-400 cursor-pointer" data-testid="ai-bed-savelib-toggle">
                          <input type="checkbox" checked={saveToLib} onChange={(e) => setSaveToLib(e.target.checked)} className="accent-[#E6B34A] w-3.5 h-3.5" data-testid="ai-bed-savelib" />
                          Save uploads to House Music Library
                        </label>
                        <button type="button" onClick={() => bedRef.current?.click()} disabled={busy === "bed"} data-testid="ai-bed-upload" className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-semibold transition-colors disabled:opacity-60">
                          {busy === "bed" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload your own music
                        </button>
                      </>
                    )}
                  </div>
                  {!bedAudio && library.length > 0 && (
                    <div data-testid="ai-bed-library">
                      <label className="overline text-zinc-400">House Music Library</label>
                      <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                        {library.map((t) => (
                          <div key={t.id} data-testid={`ai-lib-item-${t.id}`}
                            onClick={() => { setBedAudio({ path: t.path, filename: t.name }); set("music_bed", true); toast.success(`"${t.name}" selected`); }}
                            className="group flex items-center gap-2 rounded-md bg-white/5 hover:bg-white/10 px-2.5 py-1.5 cursor-pointer transition-colors">
                            <Music className="w-3.5 h-3.5 text-[#E6B34A] shrink-0" />
                            <span className="text-xs text-zinc-300 truncate flex-1">{t.name}</span>
                            <button type="button" onClick={(e) => deleteLibItem(t.id, e)} data-testid={`ai-lib-delete-${t.id}`} className="text-zinc-500 hover:text-red-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {bedAudio && (
                    <div data-testid="ai-bed-trim">
                      <div className="flex items-center justify-between">
                        <label className="overline text-zinc-400">Trim start (seconds)</label>
                        <span className="text-[10px] text-zinc-500">where the bed begins</span>
                      </div>
                      <Input type="number" min="0" max="60" step="1" value={form.bed_start}
                        onChange={(e) => set("bed_start", Math.max(0, Math.min(60, parseFloat(e.target.value) || 0)))}
                        data-testid="ai-bed-start" className={inputCls} />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="overline text-zinc-400">Music volume</label>
                      <span className="text-[11px] text-zinc-400 tabular-nums">{Math.round(form.bed_volume * 250)}%</span>
                    </div>
                    <input type="range" min="0.02" max="0.4" step="0.01" value={form.bed_volume}
                      onChange={(e) => set("bed_volume", parseFloat(e.target.value))}
                      data-testid="ai-bed-volume" className="w-full mt-1 accent-[#E6B34A] cursor-pointer" />
                    <div className="flex justify-between text-[10px] text-zinc-500"><span>Softer</span><span>Louder</span></div>
                  </div>
                </div>
              )}
              <div className="mt-2">
                <label className="overline text-zinc-400">Retake note (optional)</label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="ai-retake-presets">
                  {["Slower", "Warmer", "More energetic", "Brighter", "Softer"].map((chip) => {
                    const active = form.instructions.toLowerCase().includes(chip.toLowerCase());
                    return (
                      <button key={chip} type="button" data-testid={`ai-preset-${chip.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => set("instructions", active
                          ? form.instructions.split(",").map((s) => s.trim()).filter((s) => s && s.toLowerCase() !== chip.toLowerCase()).join(", ")
                          : [...form.instructions.split(",").map((s) => s.trim()).filter(Boolean), chip].join(", "))}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${active ? "border-[#E6B34A] bg-[#E6B34A]/15 text-[#E6B34A]" : "border-white/10 bg-white/5 text-zinc-300 hover:border-[#E6B34A]/60"}`}>{chip}</button>
                    );
                  })}
                </div>
                <Input value={form.instructions} onChange={(e) => set("instructions", e.target.value)} placeholder="e.g. slower, warmer, more energetic" data-testid="ai-retake-note" className={inputCls} />
              </div>
              <button onClick={genVoiceover} disabled={busy === "voice" || !script.trim()} data-testid="ai-gen-voice"
                className="w-full mt-2 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy === "voice" ? <Loader2 className="w-4 h-4 animate-spin" /> : (audio ? <RefreshCw className="w-4 h-4" /> : <Mic className="w-4 h-4" />)}
                {audio ? (form.instructions.trim() ? "Redo with note" : "Redo voiceover") : "Generate voiceover"}
              </button>
              {audio && <audio controls src={`${API}${audio.url}&auth=${token}`} data-testid="ai-audio" className="w-full mt-2 h-9" />}
              {audio?.voice_label && <p className="text-[11px] text-zinc-500 mt-1">Voice: {audio.voice_label}{audio.music_bed ? ` · ${audio.bed_style_label} bed` : ""}{audio.tone_tilt ? ` · ${audio.tone_tilt}` : ""}{audio.speed && audio.speed !== 1 ? ` · ${audio.speed < 1 ? "slower" : "faster"}` : ""}</p>}
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0C1338] p-3">
              <label className="overline text-zinc-400 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Sponsor logo</label>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  {logo ? <img src={`${API}${logo.url}&auth=${token}`} alt="Sponsor logo" data-testid="ai-logo-img" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-zinc-600" />}
                </div>
                <button onClick={genLogo} disabled={busy === "logo"} data-testid="ai-gen-logo"
                  className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy === "logo" ? <Loader2 className="w-4 h-4 animate-spin" /> : (logo ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
                  {logo ? "Redo logo" : "Generate logo"}
                </button>
              </div>
              <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif" onChange={uploadLogo} className="hidden" data-testid="ai-logo-input" />
              <button type="button" onClick={() => logoRef.current?.click()} disabled={busy === "logo-upload"} data-testid="ai-upload-logo"
                className="mt-2 w-full py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white text-xs font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                {busy === "logo-upload" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload your own logo
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline text-zinc-400">Plays</label>
              <Input type="number" value={form.plays} onChange={(e) => set("plays", e.target.value)} data-testid="ai-plays" className={inputCls} /></div>
            <div><label className="overline text-zinc-400">Sponsor link (optional)</label>
              <Input value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="https://sponsor.com" data-testid="ai-link" className={inputCls} /></div>
          </div>

          <div className="flex gap-2">
            <button onClick={saveDraft} disabled={busy === "draft"} data-testid="ai-save-draft"
              className="shrink-0 px-4 py-3 rounded-xl bg-white/10 text-white font-bold hover:bg-white/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {busy === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save draft
            </button>
            <button onClick={create} disabled={busy === "create" || !audio} data-testid="ai-create"
              className="flex-1 py-3 rounded-xl bg-emerald-500 text-[#04121a] font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
              Create ad spot
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
